import type { Maintenance, PortalRelease, PortalReleases } from '@central/api';
import { Card, InfoRow, PageHeader, StatusChip, color, formatDate } from '@central/ui';
import { Button } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { releasesQuery } from '../api';
import { ComposeButton } from '../components/ComposeButton';
import { Mono } from '../components/Mono';
import { QueryState } from '../components/QueryState';

/** What the customer's maintenance plan says about releases. */
function planLine(m: Maintenance, now = new Date()): string {
  if (m.endsAt && new Date(m.endsAt) <= now)
    return `Your maintenance plan ended on ${formatDate(m.endsAt)}.`;
  if (!m.coversThrough) return 'GoATMS releases, newest first.';
  const ends = m.endsAt ? ` · plan ends ${formatDate(m.endsAt)}` : '';
  return `Your maintenance plan covers releases through ${m.coversThrough}${ends}`;
}

/**
 * Releases: published GoATMS releases newest first, whether the customer's
 * maintenance covers each, and which release each deployment runs.
 */
export function Releases() {
  const { data, error } = useQuery(releasesQuery);
  return (
    <>
      <PageHeader title="Releases" subtitle={data && planLine(data.maintenance)} />
      {!data ? <QueryState error={error} /> : <ReleasesBody r={data} />}
    </>
  );
}

function ReleasesBody({ r }: { r: PortalReleases }) {
  const latest = r.items[0];
  const behind = r.deployments.filter((d) => d.behind);
  return (
    <>
      {r.deployments.length > 0 && (
        <Card
          title="Your deployments"
          action={
            latest &&
            behind.length > 0 && (
              <ComposeButton kind="question" subject={`Schedule an upgrade to ${latest.version}`}>
                Schedule upgrade
              </ComposeButton>
            )
          }
        >
          {r.deployments.map((d) => (
            <InfoRow key={d.id} label={d.name}>
              <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
                <Mono>{d.version}</Mono>
                {d.behind ? (
                  <StatusChip tone="warn">Update available</StatusChip>
                ) : (
                  <StatusChip tone="ok">Up to date</StatusChip>
                )}
              </span>
            </InfoRow>
          ))}
        </Card>
      )}
      {r.items.length === 0 ? (
        <Card>No releases have been published yet.</Card>
      ) : (
        r.items.map((rel, i) => <ReleaseCard key={rel.id} rel={rel} latest={i === 0} />)
      )}
    </>
  );
}

/** Plain-text highlights split on blank lines. Rendered as text nodes, never as HTML. */
function paragraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function ReleaseCard({ rel, latest }: { rel: PortalRelease; latest: boolean }) {
  return (
    <section
      aria-label={`Release ${rel.version}`}
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: 12,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2
          style={{
            margin: 0,
            fontSize: 18,
            fontFamily: 'var(--mantine-font-family-monospace)',
          }}
        >
          {rel.version}
        </h2>
        {latest && <StatusChip tone="blu">Latest</StatusChip>}
        {rel.covered ? (
          <StatusChip tone="ok">Covered by your plan</StatusChip>
        ) : (
          <StatusChip tone="unk">Not covered by your plan</StatusChip>
        )}
        <span style={{ fontSize: 13, color: color.textMuted }}>
          Published {formatDate(rel.publishedAt)}
        </span>
      </div>
      {rel.deployments.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {rel.deployments.map((d) => (
            <StatusChip key={d.id} tone="blu">
              {d.name} runs this
            </StatusChip>
          ))}
        </div>
      )}
      {rel.highlights &&
        paragraphs(rel.highlights).map((p, i) => (
          <p
            key={i}
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: '23px',
              color: color.textBody,
              whiteSpace: 'pre-line',
            }}
          >
            {p}
          </p>
        ))}
      {rel.notesUrl && (
        <div>
          <Button
            component="a"
            href={rel.notesUrl}
            target="_blank"
            rel="noreferrer"
            variant="default"
            h={40}
          >
            Release notes
          </Button>
        </div>
      )}
    </section>
  );
}
